import { useCallback, useEffect, useState } from 'react'
import { orderRepository } from '../../data/repositories/OrderRepositoryImpl'
import { OrderListItem, OrderStatus } from '../../domain/entities/Order'

// Dedicated hook for the Indents page — independent of useOrders/OrdersPage. Always locked to
// moq_check=false (indents that failed their own MOQ check and need admin attention), not a
// user-toggleable filter.
export const useIndents = (initialLimit = 25) => {
  const [orders, setOrders] = useState<OrderListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [catalogItemId, setCatalogItemId] = useState('')
  const [limit, setLimit] = useState(initialLimit)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const fetchIndents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await orderRepository.getOrders({
        search: search || undefined,
        catalogItemId: catalogItemId || undefined,
        moqCheck: false,
        page,
        limit,
      })
      setOrders(res.orders)
      setTotalPages(res.totalPages)
      setTotalItems(res.totalItems)
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to load indents.')
    } finally {
      setLoading(false)
    }
  }, [search, catalogItemId, page, limit])

  useEffect(() => {
    fetchIndents()
  }, [fetchIndents])

  // Deliberately does not touch `error` (the page-level fetch-error banner) — the caller's
  // modal displays this error itself, so surfacing it here too would show it twice.
  const updateStatus = async (id: string, nextStatus: OrderStatus, currentVersion: number) => {
    await orderRepository.updateOrderStatus(id, nextStatus, currentVersion)
    await fetchIndents()
  }

  const handleSetSearch = (val: string) => {
    setSearch(val)
    setPage(1)
  }

  const handleSetCatalogItemId = (val: string) => {
    setCatalogItemId(val)
    setPage(1)
  }

  const handleSetLimit = (val: number) => {
    setLimit(val)
    setPage(1)
  }

  const resetFilters = () => {
    setSearch('')
    setCatalogItemId('')
    setPage(1)
  }

  return {
    orders,
    loading,
    error,
    search,
    setSearch: handleSetSearch,
    catalogItemId,
    setCatalogItemId: handleSetCatalogItemId,
    limit,
    setLimit: handleSetLimit,
    page,
    setPage,
    totalPages,
    totalItems,
    updateStatus,
    resetFilters,
    refresh: fetchIndents,
  }
}
